import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { groupsAPI, connectionsAPI } from '../api/client';
import { Users, UserPlus, Settings, Check, X } from 'lucide-react';
import Layout from '../components/Layout';
import '../styles/group-detail.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

function stringToColor(str) {
  const colors = ['#1B3A2E', '#1A2744', '#1D3526', '#252E44', '#2d1b69', '#1a3a4a'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export default function GroupDetail() {
  const { id } = useParams();

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [editingGroup, setEditingGroup] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);

  const currentUserId = localStorage.getItem('userId');

  useEffect(() => { loadAll(); }, [id]);

  const loadAll = async () => {
    try {
      const [groupsRes, membersRes, connRes] = await Promise.all([
        groupsAPI.getAll(),
        groupsAPI.getMembers(id),
        connectionsAPI.getAll(),
      ]);
      const found = (groupsRes.data || []).find((g) => String(g.id) === String(id));
      setGroup(found || null);
      setMembers(membersRes.data || []);
      setConnections((connRes.data || []).filter((c) => c.status === 'accepted'));
    } catch {}
    setLoading(false);
  };

  const handleAddMember = async (userId) => {
    setAdding(true);
    setAddError('');
    try {
      await groupsAPI.addMember(id, userId);
      loadAll();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add member');
    }
    setAdding(false);
  };

  const handleSaveGroup = async () => {
    if (!editName.trim()) return;
    setSavingGroup(true);
    try {
      const res = await groupsAPI.update(id, { name: editName.trim(), description: editDesc.trim() });
      setGroup(res.data);
      setEditingGroup(false);
    } catch {}
    setSavingGroup(false);
  };

  const handleRemoveMember = async (userId) => {
    try {
      await groupsAPI.removeMember(id, userId);
      loadAll();
    } catch {}
  };

  const memberUserIds = new Set(members.map((m) => m.id));
  const addableConnections = connections.filter((c) => {
    const friendId = c.user_id === currentUserId ? c.connected_user_id : c.user_id;
    return !memberUserIds.has(friendId);
  });
  const isCreator = group?.creator_id === currentUserId;

  if (loading) return <Layout title="Group"><div className="gd-loading">Loading…</div></Layout>;
  if (!group) return <Layout title="Group"><div className="gd-loading">Group not found.</div></Layout>;

  return (
    <Layout title="Group">
      <div className="gd-page">

        {/* Header card */}
        <div className="gd-header-card">
          <div className="gd-group-icon" style={{ background: stringToColor(group.name) }}>
            {getInitials(group.name)}
          </div>
          <div className="gd-header-info">
            {editingGroup ? (
              <div className="gd-edit-form">
                <input
                  className="gd-edit-name"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleSaveGroup()}
                />
                <textarea
                  className="gd-edit-desc"
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  placeholder="Description (optional)"
                  rows={2}
                />
                <div className="gd-edit-actions">
                  <button className="gd-icon-btn blue" onClick={handleSaveGroup} disabled={savingGroup || !editName.trim()}>
                    <Check size={14} />
                  </button>
                  <button className="gd-icon-btn" onClick={() => setEditingGroup(false)}><X size={14} /></button>
                </div>
              </div>
            ) : (
              <>
                <div className="gd-name-row">
                  <h1 className="gd-group-name">{group.name}</h1>
                  {isCreator && (
                    <button className="gd-icon-btn" onClick={() => { setEditName(group.name); setEditDesc(group.description || ''); setEditingGroup(true); }}>
                      <Settings size={14} />
                    </button>
                  )}
                </div>
                {group.description && <p className="gd-group-desc">{group.description}</p>}
              </>
            )}
          </div>
          <div className="gd-stats">
            <div className="gd-stat">
              <span className="gd-stat-num">{members.length}</span>
              <span className="gd-stat-label">Members</span>
            </div>
          </div>
        </div>

        <div className="gd-grid">
          {/* Members */}
          <div className="gd-card">
            <div className="gd-card-header">
              <h2 className="gd-card-title"><Users size={15} /> Members</h2>
            </div>
            {members.length === 0 ? (
              <p className="gd-empty">No members yet — add your first person below.</p>
            ) : members.map(m => (
              <div key={m.id} className="gd-member-row">
                <div className="gd-member-avatar">{getInitials(m.display_name)}</div>
                <div className="gd-member-info">
                  <span className="gd-member-name">{m.display_name || 'Unnamed'}</span>
                  {m.id === group.creator_id && <span className="gd-creator-badge">creator</span>}
                </div>
                {((isCreator && m.id !== currentUserId) || m.id === currentUserId) && (
                  <button className="gd-remove-btn" onClick={() => handleRemoveMember(m.id)}>
                    {m.id === currentUserId ? 'Leave' : 'Remove'}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add friends */}
          {isCreator && addableConnections.length > 0 && (
            <div className="gd-card">
              <div className="gd-card-header">
                <h2 className="gd-card-title"><UserPlus size={15} /> Invite Friends</h2>
              </div>
              {addError && <p className="gd-add-error">{addError}</p>}
              {addableConnections.map(c => {
                const friendId = c.user_id === currentUserId ? c.connected_user_id : c.user_id;
                return (
                  <div key={c.id} className="gd-member-row">
                    <div className="gd-member-avatar">{getInitials(c.display_name)}</div>
                    <span className="gd-member-name">{c.display_name}</span>
                    <button className="gd-add-btn" onClick={() => handleAddMember(friendId)} disabled={adding}>
                      Add
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {isCreator && connections.length > 0 && addableConnections.length === 0 && (
            <p className="gd-all-added">All your friends are already in this group.</p>
          )}
        </div>
      </div>
    </Layout>
  );
}
